import { DEFAULT_PAGE_SIZE, LeadSort, listLeads, type LeadPage } from '@/api/leads'
import type { SortDirection } from '@/api/sorting'
import { usePagedList, type PagedList, type PageRange } from '@/hooks/usePagedList'

/** Which rows are on screen, counted from the page that actually came back. */
export type LeadRange = PageRange

export type LeadList = PagedList<LeadPage, LeadSort> & {
  tradeFairId: string | null
  setTradeFairId: (id: string | null) => void
  onlyMine: boolean
  setOnlyMine: (mine: boolean) => void
  /** True narrows to the leads whose company is not in the master data yet. */
  onlyUnlinked: boolean
  setOnlyUnlinked: (unlinked: boolean) => void
  /** A user id, `ASSIGNEE_NOBODY`, or null for every assignment. */
  assignee: string | null
  setAssignee: (assignee: string | null) => void
}

/**
 * The filters this screen puts in the address bar, beyond the ones every list has.
 *
 * No status: the lead's status is not shown anywhere in this application any more, and a filter
 * for a value nothing displays is a control nobody can read the result of. The backend still
 * carries the field — see `LeadSummary` — it is simply not this screen's business.
 */
const FILTERS = ['fair', 'mine', 'unlinked', 'assignee'] as const

/** How each column is read, mirroring `LeadSort` in the backend. */
const DIRECTIONS: Readonly<Record<LeadSort, SortDirection>> = {
  // Newest conversation first: at a fair the lead somebody wants is the one just written.
  [LeadSort.CAPTURED_AT]: 'DESC',
  [LeadSort.COMPANY_NAME]: 'ASC',
  [LeadSort.TRADE_FAIR]: 'ASC',
}

/**
 * There is no visibility option here on purpose. Which leads come back is the backend's decision,
 * made in the query; a client-side flag would only be able to hide rows it had already received.
 */
export function useLeadList(): LeadList {
  const list = usePagedList<LeadPage, LeadSort>({
    filterKeys: FILTERS,
    directions: DIRECTIONS,
    defaultSort: LeadSort.CAPTURED_AT,
    initialPageSize: DEFAULT_PAGE_SIZE,
    rowCount: (page) => page.leads.length,
    load: ({ search, page, size, sort, direction, filters }, signal) =>
      listLeads(
        {
          search,
          tradeFairId: filters.fair || null,
          mine: filters.mine === 'true',
          // Only ever narrows to the ones still to be taken over; there is no screen asking for
          // the opposite, so the "either" the backend defaults to is the other state.
          linked: filters.unlinked === 'true' ? false : null,
          assignee: filters.assignee || null,
          sort,
          direction,
          page,
          size,
        },
        signal,
      ),
  })

  return {
    ...list,
    tradeFairId: list.filters.fair || null,
    setTradeFairId: (id) => list.setFilter('fair', id ?? ''),
    onlyMine: list.filters.mine === 'true',
    setOnlyMine: (mine) => list.setFilter('mine', mine ? 'true' : ''),
    onlyUnlinked: list.filters.unlinked === 'true',
    setOnlyUnlinked: (unlinked) => list.setFilter('unlinked', unlinked ? 'true' : ''),
    assignee: list.filters.assignee || null,
    setAssignee: (assignee) => list.setFilter('assignee', assignee ?? ''),
  }
}
